/**
 * PerkOS-Chat client.
 *
 * Connects an agent to `wss://chat.perkos.xyz/chat`. Handles auth, automatic
 * reconnect with exponential backoff, heartbeat, persistent JSONL storage of
 * incoming and outgoing messages, and history pagination.
 *
 * This is a sibling of `RelayClient` (which speaks the A2A task protocol).
 * Same runtime conventions, different wire format — chat uses framed JSON
 * with role-aware auth instead of A2A JSON-RPC.
 *
 * Companion server: github.com/PerkOS-xyz/PerkOS-Chat
 */

import { randomUUID } from "node:crypto";

import WebSocket from "ws";

import { ChatStore, type ConversationMetadata } from "./chat-store.js";
import type {
  ChatConfig,
  ChatDeliverFrame,
  ChatFrame,
  ChatMessage,
  ChatMessageInboundFrame,
  ChannelJoinFrame,
  FarcasterInboundFrame,
  HistoryRequestFrame,
  ReceiptRequestFrame,
} from "./chat-types.js";

export interface ChatClientHandlers {
  /** Called when a user (or another agent) sent a message in a conv we participate in. */
  onChatDeliver?: (msg: ChatMessageInboundFrame | ChatDeliverFrame) => void | Promise<void>;
  /** Called when the server adds us to a new conv. */
  onChannelJoin?: (frame: ChannelJoinFrame) => void | Promise<void>;
  /** Optional logging hooks. Default routes to console.log/console.error. */
  logger?: { info: (...a: unknown[]) => void; error: (...a: unknown[]) => void };
}

const DEFAULT_URL = "wss://chat.perkos.xyz/chat";
const DEFAULT_HISTORY_LIMIT = 50;
const DEFAULT_MIN_RECONNECT = 1_000;
const DEFAULT_MAX_RECONNECT = 60_000;
const DEFAULT_HEARTBEAT_MS = 25_000;
const AUTH_TIMEOUT_MS = 10_000;

export class ChatClient {
  private ws: WebSocket | null = null;
  private readonly config: Required<Omit<ChatConfig, "storeRoot">> & { storeRoot?: string };
  private readonly agentName: string;
  private readonly handlers: ChatClientHandlers;
  private readonly logger: { info: (...a: unknown[]) => void; error: (...a: unknown[]) => void };
  private readonly store: ChatStore;

  private reconnectMs: number;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null;
  private connected = false;
  private authed = false;
  private stopped = false;

  constructor(opts: {
    agentName: string;
    config: ChatConfig;
    handlers?: ChatClientHandlers;
  }) {
    this.agentName = opts.agentName;
    const cfg = opts.config;
    this.config = {
      enabled: cfg.enabled,
      url: cfg.url || DEFAULT_URL,
      apiKey: cfg.apiKey,
      storeRoot: cfg.storeRoot,
      defaultHistoryLimit: cfg.defaultHistoryLimit ?? DEFAULT_HISTORY_LIMIT,
      minReconnectMs: cfg.minReconnectMs ?? DEFAULT_MIN_RECONNECT,
      maxReconnectMs: cfg.maxReconnectMs ?? DEFAULT_MAX_RECONNECT,
      heartbeatIntervalMs: cfg.heartbeatIntervalMs ?? DEFAULT_HEARTBEAT_MS,
    };
    this.handlers = opts.handlers ?? {};
    this.logger = this.handlers.logger ?? { info: console.log, error: console.error };
    this.store = new ChatStore({ storeRoot: this.config.storeRoot });
    this.reconnectMs = this.config.minReconnectMs;
  }

  /** Storage handle — exposed so consumers can read history without going through the wire. */
  getStore(): ChatStore {
    return this.store;
  }

  isConnected(): boolean {
    return this.connected && this.authed;
  }

  start(): void {
    if (!this.config.enabled) {
      this.logger.info("[perkos-chat] disabled in config; not connecting");
      return;
    }
    if (!this.config.apiKey) {
      this.logger.error("[perkos-chat] missing apiKey; cannot connect");
      return;
    }
    this.stopped = false;
    this.connect();
  }

  stop(): void {
    this.stopped = true;
    this.clearTimers();
    if (this.ws) {
      try {
        this.ws.close(1000, "client shutting down");
      } catch { /* ignore */ }
      this.ws = null;
    }
    this.connected = false;
    this.authed = false;
  }

  /**
   * Send a reply from this agent into a conversation. Appends to the local
   * store optimistically, then sends to the server. If the WS is offline,
   * the local store is still updated and the message is dropped from the
   * wire — callers should re-send after reconnect if guaranteed delivery is
   * required (the simple agent loop should just regenerate from history).
   */
  async sendReply(opts: {
    convId: string;
    walletAddress: string;
    text: string;
    replyTo?: string | null;
  }): Promise<{ id: string; delivered: boolean }> {
    const id = randomUUID();
    const msg: ChatMessage = {
      id,
      from: `agent:${this.agentName}`,
      text: opts.text,
      timestamp: new Date().toISOString(),
      replyTo: opts.replyTo ?? null,
    };

    await this.store.append(opts.convId, msg);

    const frame = {
      type: "chat_reply" as const,
      id,
      convId: opts.convId,
      walletAddress: opts.walletAddress,
      text: opts.text,
      replyTo: opts.replyTo ?? null,
    };
    const delivered = this.sendFrame(frame);
    return { id, delivered };
  }

  // -------------------------------------------------------------------------
  // WebSocket lifecycle
  // -------------------------------------------------------------------------

  private connect(): void {
    if (this.stopped) return;
    const url = this.config.url;
    this.logger.info(`[perkos-chat] connecting to ${url}`);

    try {
      this.ws = new WebSocket(url);
    } catch (err) {
      this.logger.error(`[perkos-chat] failed to construct WS: ${errMsg(err)}`);
      this.scheduleReconnect();
      return;
    }

    let authTimer: ReturnType<typeof setTimeout> | null = setTimeout(() => {
      if (!this.authed) {
        this.logger.error("[perkos-chat] auth timeout");
        try { this.ws?.close(4002, "auth timeout"); } catch { /* ignore */ }
      }
    }, AUTH_TIMEOUT_MS);

    this.ws.on("open", () => {
      this.connected = true;
      this.reconnectMs = this.config.minReconnectMs;
      this.sendFrame({
        type: "auth",
        role: "agent",
        agentName: this.agentName,
        apiKey: this.config.apiKey,
      });
    });

    this.ws.on("message", (data) => {
      let frame: ChatFrame;
      try {
        frame = JSON.parse(data.toString("utf8")) as ChatFrame;
      } catch {
        this.logger.error("[perkos-chat] failed to parse frame");
        return;
      }
      if (authTimer) { clearTimeout(authTimer); authTimer = null; }
      void this.handleFrame(frame);
    });

    this.ws.on("close", (code, reason) => {
      this.connected = false;
      this.authed = false;
      this.clearTimers();
      if (authTimer) { clearTimeout(authTimer); authTimer = null; }
      if (!this.stopped) {
        this.logger.info(`[perkos-chat] disconnected (${code}: ${reason?.toString() || "no reason"}); reconnecting`);
        this.scheduleReconnect();
      }
    });

    this.ws.on("error", (err) => {
      this.logger.error(`[perkos-chat] WS error: ${errMsg(err)}`);
    });
  }

  private async handleFrame(frame: ChatFrame): Promise<void> {
    switch (frame.type) {
      case "auth_ok":
        this.authed = true;
        this.logger.info(`[perkos-chat] authed as agent:${this.agentName}`);
        this.startHeartbeat();
        return;

      case "auth_error":
        this.logger.error(`[perkos-chat] auth_error ${frame.code}: ${frame.message}`);
        try { this.ws?.close(4401, frame.code); } catch { /* ignore */ }
        return;

      case "chat_deliver":
      case "chat_message": {
        await this.persistInbound(frame);
        if (this.handlers.onChatDeliver) {
          try {
            await this.handlers.onChatDeliver(frame);
          } catch (err) {
            this.logger.error(`[perkos-chat] onChatDeliver threw: ${errMsg(err)}`);
          }
        }
        return;
      }

      case "farcaster.inbound":
        await this.deliverFarcasterInbound(frame);
        return;

      case "history_request":
        await this.respondToHistoryRequest(frame);
        return;

      case "receipt_request":
        await this.respondToReceiptRequest(frame);
        return;

      case "channel_join":
        await this.handleChannelJoin(frame);
        return;

      case "ack":
      case "pong":
      case "typing":
        return;

      case "error":
        this.logger.error(`[perkos-chat] server error ${frame.code}: ${frame.message}`);
        return;

      default:
        // Unknown — log for visibility but don't blow up
        this.logger.info(`[perkos-chat] unknown frame type: ${(frame as { type: string }).type}`);
    }
  }

  /**
   * Forward an inbound Farcaster cast to the agent's LOCAL Farcaster adapter.
   * The adapter binds 0.0.0.0:8645 inside the same ECS task, so it's reachable
   * on loopback from this co-located bridge. We forward the RAW body + the
   * X-Neynar-Signature header; the adapter verifies the Neynar HMAC (it holds
   * FARCASTER_WEBHOOK_SECRET) — the relay + bridge are just the transport.
   */
  private async deliverFarcasterInbound(frame: FarcasterInboundFrame): Promise<void> {
    if (typeof frame.body !== "string") return;
    const port = process.env.FARCASTER_WEBHOOK_PORT ?? "8645";
    const url = `http://127.0.0.1:${port}/webhooks/farcaster`;
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          "X-Neynar-Signature": frame.signature ?? "",
        },
        body: frame.body,
      });
      if (!res.ok && res.status !== 202) {
        this.logger.error(`[perkos-chat] farcaster adapter returned ${res.status}`);
      }
    } catch (err) {
      this.logger.error(
        `[perkos-chat] farcaster delivery to local adapter failed: ${errMsg(err)}`,
      );
    }
  }

  private async persistInbound(frame: ChatDeliverFrame | ChatMessageInboundFrame): Promise<void> {
    const msg: ChatMessage = {
      id: frame.id,
      from: frame.from,
      text: frame.text,
      timestamp: frame.timestamp,
      replyTo: "replyTo" in frame ? frame.replyTo ?? null : null,
    };
    try {
      await this.store.append(frame.convId, msg);
    } catch (err) {
      this.logger.error(`[perkos-chat] failed to append to store: ${errMsg(err)}`);
    }
  }

  private async handleChannelJoin(frame: ChannelJoinFrame): Promise<void> {
    const now = new Date().toISOString();
    const existing = await this.store.readMetadata(frame.convId);
    const meta: ConversationMetadata = {
      convId: frame.convId,
      participants: frame.participants,
      historyHost: frame.historyHost,
      createdAt: existing?.createdAt ?? now,
      updatedAt: now,
    };
    try {
      await this.store.writeMetadata(meta);
    } catch (err) {
      this.logger.error(`[perkos-chat] failed to write metadata for ${frame.convId}: ${errMsg(err)}`);
    }
    if (this.handlers.onChannelJoin) {
      try {
        await this.handlers.onChannelJoin(frame);
      } catch (err) {
        this.logger.error(`[perkos-chat] onChannelJoin threw: ${errMsg(err)}`);
      }
    }
  }

  private async respondToReceiptRequest(frame: ReceiptRequestFrame): Promise<void> {
    let summary;
    try {
      summary = await this.store.computeReceipt(frame.convId);
    } catch (err) {
      this.logger.error(`[perkos-chat] receipt computation failed: ${errMsg(err)}`);
      return;
    }
    if (!summary) {
      // No jsonl for this conv → nothing to hash. Reply with an empty hash
      // so the user sees a definitive "no content" rather than a timeout.
      this.sendFrame({
        type: "receipt_response",
        id: frame.id,
        convId: frame.convId,
        forWallet: frame.forWallet,
        transcriptHash: "",
        hashAlgo: "sha256",
        messageCount: 0,
        firstMessageAt: null,
        lastMessageAt: null,
        generatedAt: new Date().toISOString(),
      });
      return;
    }
    this.sendFrame({
      type: "receipt_response",
      id: frame.id,
      convId: frame.convId,
      forWallet: frame.forWallet,
      transcriptHash: summary.transcriptHash,
      hashAlgo: summary.hashAlgo,
      messageCount: summary.messageCount,
      firstMessageAt: summary.firstMessageAt,
      lastMessageAt: summary.lastMessageAt,
      generatedAt: new Date().toISOString(),
    });
  }

  private async respondToHistoryRequest(frame: HistoryRequestFrame): Promise<void> {
    const limit = frame.limit ?? this.config.defaultHistoryLimit;
    let page;
    try {
      page = await this.store.readPage(frame.convId, { before: frame.before ?? null, limit });
    } catch (err) {
      this.logger.error(`[perkos-chat] history read failed: ${errMsg(err)}`);
      page = { messages: [], hasMore: false };
    }
    this.sendFrame({
      type: "history_chunk",
      id: frame.id,
      convId: frame.convId,
      forWallet: frame.forWallet,
      messages: page.messages,
      hasMore: page.hasMore,
    });
  }

  private sendFrame(frame: ChatFrame): boolean {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(frame));
      return true;
    }
    return false;
  }

  private startHeartbeat(): void {
    if (this.heartbeatTimer) clearInterval(this.heartbeatTimer);
    this.heartbeatTimer = setInterval(() => {
      if (!this.sendFrame({ type: "ping" })) {
        // Socket gone; close() will trigger reconnect.
      }
    }, this.config.heartbeatIntervalMs);
  }

  private scheduleReconnect(): void {
    if (this.stopped) return;
    const delay = this.reconnectMs;
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
    this.reconnectMs = Math.min(delay * 2, this.config.maxReconnectMs);
  }

  private clearTimers(): void {
    if (this.heartbeatTimer) { clearInterval(this.heartbeatTimer); this.heartbeatTimer = null; }
    if (this.reconnectTimer) { clearTimeout(this.reconnectTimer); this.reconnectTimer = null; }
  }
}

function errMsg(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}
